/** A region bake's `meta.frame` (tools/dtm/bake_region.py `region_frame`) and the polar grid's region-local metres. */

export interface RegionProjection {
  m_per_deg_lat: number;
  m_per_deg_lon: number;
  /** 'equirectangular' or 'polar_stereographic'; absent on older bakes, which are equirectangular. */
  projection?: string;
  /** +1 north, -1 south, on a polar grid. */
  pole?: number;
  radius_m?: number;
  /** The polar grid's own meridian, which the bake runs through the site centre so its rows run north-south there. */
  central_meridian_deg?: number;
}

export interface PolarFrame {
  readonly pole: 1 | -1;
  readonly radiusM: number;
  readonly centralMeridianDeg: number;
}

const RAD = Math.PI / 180;

/** The polar grid a frame is on, or null for an equirectangular one. */
export function polarFrameOf(f: Partial<RegionProjection> | undefined | null): PolarFrame | null {
  if (f?.projection !== 'polar_stereographic') return null;
  const { pole, radius_m: radiusM, central_meridian_deg: cm } = f;
  if ((pole !== 1 && pole !== -1) || !(radiusM !== undefined && radiusM > 0) || cm === undefined) {
    throw new Error('polar_stereographic frame needs pole +-1, radius_m and central_meridian_deg');
  }
  return { pole, radiusM, centralMeridianDeg: cm };
}

/** Projected metres, the bake's `polar_xy`: x east and y north along the central meridian. */
function polarXY(p: PolarFrame, latDeg: number, lonDeg: number): [number, number] {
  const dl = (lonDeg - p.centralMeridianDeg) * RAD;
  const rho = 2 * p.radiusM * Math.tan(Math.PI / 4 - (p.pole * latDeg * RAD) / 2);
  return [rho * Math.sin(dl), -p.pole * rho * Math.cos(dl)];
}

/** Region-local metres for a lat/lon, about the region centre. */
export function polarRegionXZ(
  p: PolarFrame, centreLatDeg: number, centreLonDeg: number, latDeg: number, lonDeg: number,
): { x: number; z: number } {
  const [xc, yc] = polarXY(p, centreLatDeg, centreLonDeg);
  const [x, y] = polarXY(p, latDeg, lonDeg);
  return { x: x - xc, z: yc - y };
}

/** Lat/lon, longitude in [0, 360), for region-local metres: `polarRegionXZ` the other way round. */
export function polarRegionLatLon(
  p: PolarFrame, centreLatDeg: number, centreLonDeg: number, xM: number, zM: number,
): { lat: number; lon: number } {
  const [xc, yc] = polarXY(p, centreLatDeg, centreLonDeg);
  const x = xc + xM;
  const y = yc - zM;
  const rho = Math.hypot(x, y);
  const lat = p.pole * (90 - (2 * Math.atan(rho / (2 * p.radiusM))) / RAD);
  const lon = p.centralMeridianDeg + Math.atan2(x, -p.pole * y) / RAD;
  return { lat, lon: ((lon % 360) + 360) % 360 };
}

/** Lat/lon for region-local metres about an anchor: the polar grid's own inverse, else degrees per datum metre. */
export function regionOffsetLatLon(
  frame: Partial<RegionProjection> | undefined | null, datumM: number,
  anchorLatDeg: number, anchorLonDeg: number, xM: number, zM: number,
): { lat: number; lon: number } {
  const polar = polarFrameOf(frame);
  if (polar) return polarRegionLatLon(polar, anchorLatDeg, anchorLonDeg, xM, zM);
  const degPerM = 180 / (Math.PI * datumM);
  return {
    lat: anchorLatDeg - zM * degPerM,
    lon: anchorLonDeg + xM * (degPerM / Math.cos(anchorLatDeg * RAD)),
  };
}
